import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  SelectChangeEvent,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Accordion from "react-bootstrap/Accordion";
import AccordionContext from "react-bootstrap/AccordionContext";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { CompactSelectProps, CompactTextFieldProps, ErrorsType, FormDataType, OptionType, TouchedType } from "../../Types/CostObject.type";
import FilterField, { DateFilterField, FilterAutocomplete, FilterTextField } from "../Trading/Filters/FilterField";

const STATUSES: OptionType[] = [
  { value: "PENDING", label: "Pending" },
  { value: "COMMITTED", label: "Committed" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];
const getTodayDate = (): string => new Date().toISOString().split("T")[0];

export const FIELD_ROW_GRID_SX = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  mb: 1,
  "& > *": {
    flex: "1 1 170px",
    minWidth: 0, 
  },
};

const compactFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    fontSize: "0.7rem",
    height: "32px",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.9rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -8px) scale(0.74)",
      backgroundColor: "white",
      paddingRight: "0.4rem",
      fontWeight: "bold",
      color: "black",
      letterSpacing: "normal",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b9baeb85",
    borderWidth: "2px",
  },
};

const compactFieldSxError = {
  ...compactFieldSx,
  "& .MuiInputLabel-root": {
    ...compactFieldSx["& .MuiInputLabel-root"],
    "&.MuiInputLabel-shrink": {
      ...(compactFieldSx["& .MuiInputLabel-root"] as any)["&.MuiInputLabel-shrink"],
      color: "#d32f2f",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
    borderWidth: "2px",
  },
};

// ─── CompactSelect ────────────────────────────────────────────────────────────
const CompactSelect: React.FC<CompactSelectProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  sx,
  slotProps,
  onBlur,
  error = false,
  helperText,
}) => {
  const sortedOptions = [...options]
    .filter((o) => o?.label != null)
    .sort((a, b) => a.label.localeCompare(b.label)); const dynamicLabelProps = value
      ? { shrink: true, ...slotProps?.inputLabel }
      : slotProps?.inputLabel || {};

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      disabled={disabled}
      sx={{ ...(error ? compactFieldSxError : compactFieldSx), ...sx }}
    >
      <InputLabel {...dynamicLabelProps}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={onChange}
        onBlur={onBlur}
        displayEmpty
        sx={{ "& .MuiSelect-select": { py: 0.5, color: value ? "inherit" : "#999" } }}
      >
        {!value && (
          <MenuItem value="" disabled sx={{ fontSize: "0.7rem", color: "#999" }}>
            Select
          </MenuItem>
        )}
        {sortedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.7rem" }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontSize: "0.65rem", margin: "3px 0 0 0" }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// ─── CompactTextField ─────────────────────────────────────────────────────────
const CompactTextField: React.FC<CompactTextFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  sx,
  slotProps,
  error = false,
  helperText,
  onBlur,
  multiline = false,
  rows = 1,
  placeholder,
}) => {
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      type === "number" &&
      (event.key === "e" || event.key === "E" || event.key === "+")
    ) {
      event.preventDefault();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (type === "number") {
      if (/^-?\d*\.?\d*$/.test(inputValue) || inputValue === "") {
        onChange(event);
      }
    } else {
      onChange(event);
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      type={type}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      InputLabelProps={{
        shrink: type === "date" ? true : slotProps?.inputLabel?.shrink,
      }}
      InputProps={{
        onKeyPress: handleKeyPress,
        inputMode: type === "number" ? "numeric" : undefined,
        sx: { fontSize: "0.7rem", height: multiline ? "auto" : "32px" },
      }}
      sx={{
        ...(error ? compactFieldSxError : compactFieldSx),
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: multiline ? "auto" : "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        ...sx,
      }}
    />
  );
};

// ─── Plus icon shown only when the accordion panel is collapsed ─────────────
const AccordionPlusIcon: React.FC<{ eventKey: string }> = ({ eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const isCollapsed = activeEventKey !== eventKey;

  if (!isCollapsed) return null; 

  return (
    <AddIcon
      fontSize="small"
      sx={{
        position: "absolute",
        right: "1.25rem",
        top: "50%",
        transform: "translateY(-50%)",
        color: "inherit",
        pointerEvents: "none",
      }}
    />
  );
};

// ─── Main Form Component ──────────────────────────────────────────────────────
const CostObjectForm: React.FC<any> = ({ dataVersion, setDataVersion, copiedData }) => {
  const queryClient = useQueryClient();
  const [isFormModified, setIsFormModified] = useState(false);
  const [touched, setTouched] = useState<TouchedType>({});
  const [errors, setErrors] = useState<ErrorsType>({});
  const [accordionKey, setAccordionKey] = useState<string | null>(null);
  const [bookOptions, setBookOptions] = useState<OptionType[]>([]);
  const [traderOptions, setTraderOptions] = useState<OptionType[]>([]);
  const [strategyOptions, setStrategyOptions] = useState<OptionType[]>([]);
  const [clearerOptions, setClearerOptions] = useState<OptionType[]>([]);
  const [brokerOptions, setBrokerOptions] = useState<OptionType[]>([]);
  const [productOptions, setProductOptions] = useState<OptionType[]>([]);
  const [counterpartyOptions, setCounterpartyOptions] = useState<OptionType[]>([]);
  const [pnlCategoryOptions, setPnlCategoryOptions] = useState<OptionType[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<OptionType[]>([]);
  const [costTypeOptions, setCostTypeOptions] = useState<OptionType[]>([]);


  const [formData, setFormData] = useState<FormDataType>({
    type: "Lump Sum",
    costType: "",
    amount: "",
    currency: "USD",
    dueDate: "",
    effectiveDate: getTodayDate(),
    book: [],
    trader: [],
    strategy: [],
    clearer: [],
    broker: [],
    product: [],
    counterparty: [],
    pnlCategory: [],
    requiresSettlement: "",
    excludeFromPNL: "yes",
    description: "",
    status: "PENDING",
  });

  useEffect(() => {
    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Book-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setBookOptions(
          results.map((item: any) => ({
            value: item.id ?? item.name ?? item.book,
            label: item.name ?? item.book ?? item.label,
          }))
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Currency-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setCurrencyOptions(
          results
            .map((item: any) => ({
              value: item.currency_code ?? "",
              label: item.currency_code ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Strategy-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setStrategyOptions(
          results.map((item: any) => ({
            value: item.id ?? item.name ?? item.strategy,
            label: item.name ?? item.strategy ?? item.label,
          }))
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Strategy-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setStrategyOptions(
          results.map((item: any) => ({
            value: item.id ?? item.name ?? item.strategy,
            label: item.name ?? item.strategy ?? item.label,
          }))
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Trader-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setTraderOptions(
          results
            .map((item: any) => ({
              value: item.id ?? "",
              label: item.email ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Type-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setCostTypeOptions(
          results
            .map((item: any) => ({
              value: item.id ?? "",
              label: item.cost_name ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Broker-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setBrokerOptions(
          results.map((item: any) => ({
            value: item.id ?? item.name ?? item.broker,
            label: item.name ?? item.broker ?? item.label,
          }))
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Clearer-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setClearerOptions(
          results.map((item: any) => ({
            value: item.id ?? item.name ?? item.clearer,
            label: item.name ?? item.clearer ?? item.label,
          }))
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-Contract-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setProductOptions(
          results
            .map((item: any) => ({
              value: item.id ?? "",
              label: item.contract_name ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/Cost-CounterParty-Dropdown/`)
      .then((res) => {
        const results = res.data?.results ?? res.data ?? [];
        setCounterpartyOptions(
          results
            .map((item: any) => ({
              value: item.id ?? "",
              label: item.customer_company_name ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });

    axiosInstance.get(`${backendbaseUrl}/paper-app/PnL-Category-Dropdown/`)
      .then((res) => {
        const results = res.data?.data ?? res.data?.results ?? res.data ?? [];
        setPnlCategoryOptions(
          results
            .map((item: any) => ({
              value: item.value ?? item.id ?? "",
              label: item.label ?? item.name ?? item.category_name ?? "",
            }))
            .filter((opt: any) => opt.label)
        );
      })
      .catch(() => { });
  }, [dataVersion]);

  useEffect(() => {
    console.log("useEffect copiedData", copiedData);
    if (!copiedData) {
      setFormData({
        type: "Lump Sum",
        costType: "",
        amount: "",
        currency: "USD",
        dueDate: "",
        effectiveDate: getTodayDate(),
        book: [],
        trader: [],
        strategy: [],
        clearer: [],
        broker: [],
        product: [],
        counterparty: [],
        pnlCategory: [],
        requiresSettlement: "",
        excludeFromPNL: "yes",
        description: "",
        status: "PENDING",
      });
      setIsFormModified(false);
      setTouched({});
      setErrors({});
      return;
    }

    const map: Partial<FormDataType> = {
      costType: copiedData.cost_type || "",
      amount: copiedData.amount != null ? copiedData.amount.toString() : "",
      currency: "USD",
      dueDate: copiedData.due_date || "",
      effectiveDate: copiedData.effective_date || "",
      book: copiedData.book ? [copiedData.book] : [],
      trader: copiedData.trader ? [copiedData.trader] : [],
      strategy: copiedData.strategy ? [copiedData.strategy] : [],
      clearer: copiedData.clearer ? [copiedData.clearer] : [],
      broker: copiedData.broker ? [copiedData.broker] : [],
      product: copiedData.product ? [copiedData.product] : [],
      counterparty: copiedData.counterparty ? [copiedData.counterparty] : [],
      pnlCategory: (() => {
        const val =
          copiedData.PnL_Category_name ||
          copiedData.PnL_Category ||
          copiedData.pnl_category ||
          null;
        return val ? [val] : [];
      })(),
      requiresSettlement: copiedData.requires_settlement === false ? "no" : "yes",
      excludeFromPNL: copiedData.exclude_from_pnl === true ? "yes" : "no",
      description: copiedData.description || "",
      status: copiedData.status || "PENDING",
    };

    const updatedFormData = { ...formData };
    Object.entries(map).forEach(([key, value]) => {
      if (value !== null && value !== "") {
        updatedFormData[key as keyof FormDataType] = value as string;
      }
    });

    setFormData(updatedFormData);
    setIsFormModified(true);
    setTouched({});
    setErrors({});
    setAccordionKey("0");
  }, [copiedData]);

  const requiredFields: (keyof FormDataType)[] = [
    "costType", "amount", "currency", "book", "trader",
    "strategy", "clearer", "broker", "product", "counterparty","pnlCategory"
  ];

  const fieldLabels: Record<string, string> = {
    dueDate: "Due Date",
    costType: "Cost Type",
    amount: "Amount",
    currency: "Currency",
    book: "Book",
    trader: "Trader",
    strategy: "Strategy",
    clearer: "Clearer",
    broker: "Broker",
    product: "Product",
    counterparty: "Counterparty",
    pnlCategory: "PNL Category",
  };


  const validateField = (field: keyof FormDataType, value: any): string => {
    if (requiredFields.includes(field)) {
      if (Array.isArray(value)) {
        if (!value.length) return `${fieldLabels[field as string] || field} is required`;
      } else if (!value || value.toString().trim() === "") {
        return `${fieldLabels[field as string] || field} is required`;
      }
    }
    if (field === "amount" && value && isNaN(Number(value))) {
      return "Amount must be a valid number";
    }
    if (field === "amount" && value && Number(value) === 0) {
      return "Amount cannot be zero";
    }
    return "";
  };

  const validateAllFields = (): boolean => {
    const newErrors: ErrorsType = {};
    let isValid = true;
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field as string] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleBlur = (field: keyof FormDataType) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleTextChange =
    (field: keyof FormDataType) => (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      const error = validateField(field, newValue);
      setErrors((prev) => ({ ...prev, [field]: error }));
    };
  const handleMultiSelectChange =
    (field: keyof FormDataType) => (_field: string, selectedValues: string[]) => {
      setFormData((prev) => ({ ...prev, [field]: selectedValues }));
      setIsFormModified(true);
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handleSelectChange =
    (field: keyof FormDataType) => (event: SelectChangeEvent) => {
      const newValue = event.target.value;
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };
  const handleAutocompleteChange =
    (field: keyof FormDataType) => (newValue: string) => {
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handleDateFieldChange =
    (field: keyof FormDataType) => (newValue: string) => {
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handleRadioChange =
    (field: keyof FormDataType) => (event: ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
      setIsFormModified(true);
    };

  const generateId = () =>
    `CO-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

  const resetFormData = () => {
    setFormData({
      type: "Lump Sum",
      costType: "",
      amount: "",
      currency: "USD",
      dueDate: "",
      effectiveDate: getTodayDate(),
      book: [],
      trader: [],
      strategy: [],
      clearer: [],
      broker: [],
      product: [],
      counterparty: [],
      pnlCategory: [],
      requiresSettlement: "yes",
      excludeFromPNL: "no",
      description: "",
      status: "PENDING",
    });
    setTouched({});
    setErrors({});
    setIsFormModified(false);
  };

  const handleClearForm = () => resetFormData();

  const SubmitFormdata = async (e: any) => {
    e.preventDefault();

    const allTouched: TouchedType = {};
    requiredFields.forEach((field) => {
      allTouched[field as string] = true;
    });
    setTouched(allTouched);

    if (!validateAllFields()) {
      toast.error("Please complete all required fields before submitting");
      return;
    }

    const payload = {
      id: generateId(),
      status: formData.status,
      Booking_Type: formData.type,
      cost_type: formData.costType,
      amount: Number(formData.amount),
      currency: formData.currency,
      due_date: formData.dueDate || null,
      effective_date: formData.effectiveDate || null, 
      book: Array.isArray(formData.book) ? formData.book[0] || null : formData.book,
      trader: Array.isArray(formData.trader) ? formData.trader[0] || null : formData.trader,
      strategy: Array.isArray(formData.strategy) ? formData.strategy[0] || null : formData.strategy,
      clearer: Array.isArray(formData.clearer) ? formData.clearer[0] || null : formData.clearer,
      broker: Array.isArray(formData.broker) ? formData.broker[0] || null : formData.broker,
      product: Array.isArray(formData.product) ? formData.product[0] || null : formData.product,
      counterparty: Array.isArray(formData.counterparty) ? formData.counterparty[0] || null : formData.counterparty,
      PnL_Category: Array.isArray(formData.pnlCategory)
        ? formData.pnlCategory[0] || null
        : formData.pnlCategory || null, 
      requires_settlement: formData.requiresSettlement === "yes",
      exclude_from_pnl: formData.excludeFromPNL === "yes",
      description: formData.description || null,
    };

    try {
      await axiosInstance.post(
        `${backendbaseUrl}/paper-app/cost-booking/`,
        payload
      );
      toast.success("✅ Cost Object created successfully!");
      setDataVersion(dataVersion + 1);
      resetFormData();
      setAccordionKey(null);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        JSON.stringify(error.response?.data) ||
        "Failed to create Cost Object";
      toast.error(`Error: ${errorMessage}`);
      console.error("Submit error:", error.response?.data);
    }
  };

  return (
    <Box
      sx={{
        "& .accordion-button.collapsed::after": {
          backgroundImage: "none !important",
          width: 0,
          height: 0,
          margin: 0,
        },
      }}
    >
      <Accordion activeKey={accordionKey} onSelect={(key: any) => setAccordionKey(key)} className="table-ad__addInputs">
        <Accordion.Item eventKey="0">
        <Accordion.Header className="accordion_button" style={{ position: "relative" }}>
          Add Cost
          <AccordionPlusIcon eventKey="0" />
        </Accordion.Header>
        <Accordion.Body>
          <Box
            sx={{
              width: "100%",
              p: { xs: 1, sm: 1 },
              pt: "20px !important",
              backgroundColor: "#ffff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box sx={FIELD_ROW_GRID_SX}>
              <DateFilterField
                label="Effective Date"
                value={formData.effectiveDate}
                onChange={handleDateFieldChange("effectiveDate")}
              />
              {/* <DateFilterField
                label="Due Date"
                value={formData.dueDate}
                onChange={handleDateFieldChange("dueDate")}
                error={touched.dueDate && !!errors.dueDate}
                helperText={touched.dueDate ? errors.dueDate : ""}
              /> */}
              <FilterAutocomplete
                label="Status"
                options={STATUSES}
                value={formData.status}
                onChange={handleAutocompleteChange("status")}
              />

              <FilterAutocomplete
                label="Type"
                options={[{ value: "Lump Sum", label: "Lump Sum" }]}
                value={formData.type}
                onChange={handleAutocompleteChange("type")}
              />
              <FilterAutocomplete
                label="Cost Type"
                options={costTypeOptions}
                value={formData.costType}
                onChange={handleAutocompleteChange("costType")}
                required
                error={touched.costType && !!errors.costType}
                helperText={touched.costType ? errors.costType : ""}
              />


              <FilterTextField
                label="Amount"
                value={formData.amount}
                onChange={handleAutocompleteChange("amount")}
                type="number"
                required
                error={touched.amount && !!errors.amount}
                helperText={touched.amount ? errors.amount : ""}
              />
              <FilterAutocomplete
                label="Currency"
                options={[{ value: "USD", label: "USD" }]}
                value={formData.currency}
                onChange={handleAutocompleteChange("currency")}
                error={touched.currency && !!errors.currency}
                helperText={touched.currency ? errors.currency : ""}
                disabled
              />
                <FilterAutocomplete
                  label="P&L Category / Apply To"
                  options={pnlCategoryOptions}
                  value={(formData.pnlCategory as string[])?.[0] || ""}
                  onChange={handleAutocompleteChange("pnlCategory")}
                  required
                  error={touched.pnlCategory && !!errors.pnlCategory}
                  helperText={touched.pnlCategory ? errors.pnlCategory : ""}
                />

            </Box>

            <Box sx={FIELD_ROW_GRID_SX}>
              {[
                { label: "Book", field: "book" as const, options: bookOptions },
                { label: "Trader", field: "trader" as const, options: traderOptions },
                { label: "Strategy", field: "strategy" as const, options: strategyOptions },
                { label: "Clearer", field: "clearer" as const, options: clearerOptions },
                { label: "Broker", field: "broker" as const, options: brokerOptions },
                { label: "Product", field: "product" as const, options: productOptions },
                { label: "Counterparty", field: "counterparty" as const, options: counterpartyOptions },
              ].map(({ label, field, options }) => (
                <Box key={field} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <FilterAutocomplete
                    label={label}
                    options={options}
                    value={(formData[field] as string[])?.[0] || ""}
                    onChange={(newValue: string) =>
                      handleMultiSelectChange(field)(field, newValue ? [newValue] : [])
                    }
                    openOnFocus
                  />
                  {touched[field] && errors[field] && (
                    <span style={{ color: "#d32f2f", fontSize: "0.65rem", marginTop: "2px" }}>
                      {errors[field]}
                    </span>
                  )}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
                alignItems: "flex-start",
              }}
            >
              <FormControl component="fieldset" sx={{ flex: "0 0 auto" }}>
                <FormLabel
                  component="legend"
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#333", mb: 0.25 }}
                >
                  Requires Settlement
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.requiresSettlement}
                  onChange={handleRadioChange("requiresSettlement")}
                >
                  <FormControlLabel value="yes" control={<Radio size="small" sx={{ py: 0.25 }} />} label={<span style={{ fontSize: "0.75rem" }}>Yes</span>} />
                  <FormControlLabel value="no" control={<Radio size="small" sx={{ py: 0.25 }} />} label={<span style={{ fontSize: "0.75rem" }}>No</span>} />
                </RadioGroup>
              </FormControl>

              <FormControl component="fieldset" sx={{ flex: "0 0 auto" }}>
                <FormLabel
                  component="legend"
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#333", mb: 0.25 }}
                >
                  Include In PNL
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.excludeFromPNL}
                  onChange={handleRadioChange("excludeFromPNL")}
                >
                  <FormControlLabel value="yes" control={<Radio size="small" sx={{ py: 0.25 }} />} label={<span style={{ fontSize: "0.75rem" }}>Yes</span>} />
                  <FormControlLabel value="no" control={<Radio size="small" sx={{ py: 0.25 }} />} label={<span style={{ fontSize: "0.75rem" }}>No</span>} />
                </RadioGroup>
              </FormControl>

              {/* Description grows to fill remaining space */}
              <Box sx={{ flex: "1 1 260px", minWidth: 200 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={formData.description}
                  onChange={handleTextChange("description")}
                  placeholder="Enter description..."
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { fontSize: "0.7rem" } }}
                  sx={{
                    "& .MuiInputBase-root": { fontWeight: "bold", height: "48px", alignItems: "flex-start" },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, -8px) scale(0.74)",
                        backgroundColor: "white",
                        paddingRight: "0.4rem",
                        fontWeight: "bold",
                        color: "black",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b9baeb85",
                      borderWidth: "2px",
                    },
                  }} />
              </Box>

              <Box sx={{ display: "flex", gap: 1, flex: "0 0 auto", mt: 1, flexWrap: "wrap" }}>
                <Button
                  onClick={SubmitFormdata}
                  variant="contained"
                  sx={{
                    height: "32px",
                    minWidth: "100px",
                    backgroundColor: "#00bcd4",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    "&:hover": { backgroundColor: "#008c9e" },
                  }}
                >
                  Add
                </Button>
                <Button
                  onClick={handleClearForm}
                  variant="outlined"
                  disabled={!isFormModified}
                  sx={{
                    height: "32px",
                    minWidth: "100px",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    borderColor: "#e0e0e0",
                    color: isFormModified ? "#222" : "#999",
                    backgroundColor: isFormModified ? "#e6f3f5" : "#f5f5f5",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      borderColor: isFormModified ? "#008c9e" : "#e0e0e0",
                      backgroundColor: isFormModified ? "#d1e8eb" : "#f5f5f5",
                      transform: isFormModified ? "scale(1.02)" : "none",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#e0e0e0",
                      backgroundColor: "#f5f5f5",
                      color: "#999",
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Box>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    </Box>
  );
};

export default CostObjectForm;